import React, { useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2, Upload } from 'lucide-react';
import landingService from '../../../services/landingService';
import { LANDING_ICON_NAMES, renderLandingIcon } from '../../LandingPage/landingIcons';
import { BUILTIN_IMAGES, resolveImage } from '../../LandingPage/landingDefaults';

interface FieldShellProps {
  label: string;
  hint?: string;
  children: React.ReactNode;
  wide?: boolean;
}

const FieldShell: React.FC<FieldShellProps> = ({ label, hint, children, wide }) => (
  <label className={`mlp-field ${wide ? 'mlp-field-wide' : ''}`}>
    <span className="mlp-label">{label}</span>
    {children}
    {hint && <span className="mlp-hint">{hint}</span>}
  </label>
);

export const TextField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  wide?: boolean;
}> = ({ label, value, onChange, placeholder, hint, wide }) => (
  <FieldShell label={label} hint={hint} wide={wide}>
    <input
      type="text"
      className="mlp-input"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </FieldShell>
);

export const NumberField: React.FC<{
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  hint?: string;
}> = ({ label, value, onChange, min, max, hint }) => (
  <FieldShell label={label} hint={hint}>
    <input
      type="number"
      className="mlp-input"
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  </FieldShell>
);

export const TextAreaField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  hint?: string;
  wide?: boolean;
}> = ({ label, value, onChange, rows = 3, hint, wide = true }) => (
  <FieldShell label={label} hint={hint} wide={wide}>
    <textarea
      className="mlp-input mlp-textarea"
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </FieldShell>
);

export const ToggleField: React.FC<{
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <label className="mlp-toggle">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span className="mlp-toggle-track" aria-hidden="true" />
    <span>{label}</span>
  </label>
);

export const SelectField: React.FC<{
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  hint?: string;
  disabled?: boolean;
}> = ({ label, value, options, onChange, hint, disabled }) => (
  <FieldShell label={label} hint={hint}>
    <select
      className="mlp-input"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </FieldShell>
);

export const IconField: React.FC<{
  label?: string;
  value: string;
  onChange: (v: string) => void;
}> = ({ label = 'Icon', value, onChange }) => (
  <FieldShell label={label}>
    <span className="mlp-icon-select">
      <span className="mlp-icon-preview">{renderLandingIcon(value, 'w-4 h-4')}</span>
      <select className="mlp-input" value={value} onChange={(e) => onChange(e.target.value)}>
        {LANDING_ICON_NAMES.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
    </span>
  </FieldShell>
);

const BUILTIN_LABELS: Record<string, string> = {
  'builtin:logo': 'EasyEAuction logo',
  'builtin:logistic': 'Logistics (hero)',
  'builtin:industrial': 'Industrial (hero)',
  'builtin:civil': 'Civil & construction (hero)',
  'builtin:about': 'Who We Are photo',
};

export const ImageField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  onError: (message: string) => void;
}> = ({ label, value, onChange, hint, onError }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const preview = resolveImage(value);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      onChange(await landingService.uploadImage(file));
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mlp-field mlp-field-wide">
      <span className="mlp-label">{label}</span>
      <div className="mlp-image-field">
        <div className="mlp-image-preview">
          {preview ? <img src={preview} alt="" /> : <span>No image</span>}
        </div>
        <div className="mlp-image-controls">
          <div className="mlp-image-actions">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              hidden
              onChange={handleFile}
            />
            <button
              type="button"
              className="mlp-btn mlp-btn-sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              <Upload />
              {uploading ? 'Uploading...' : 'Upload image'}
            </button>
            <select
              className="mlp-input mlp-input-sm"
              value=""
              onChange={(e) => e.target.value && onChange(e.target.value)}
              aria-label="Use a built-in image"
            >
              <option value="">Use built-in image…</option>
              {Object.keys(BUILTIN_IMAGES).map((token) => (
                <option key={token} value={token}>
                  {BUILTIN_LABELS[token] || token}
                </option>
              ))}
            </select>
          </div>
          <input
            type="text"
            className="mlp-input"
            value={value.startsWith('builtin:') ? '' : value}
            placeholder={value.startsWith('builtin:') ? 'Built-in image in use — or paste an image URL' : 'Image URL'}
            onChange={(e) => onChange(e.target.value)}
          />
          {hint && <span className="mlp-hint">{hint}</span>}
        </div>
      </div>
    </div>
  );
};

// A reorderable list of repeated items (slides, steps, links ...).
export function ListEditor<T>({
  items,
  onChange,
  newItem,
  addLabel,
  itemLabel,
  renderItem,
  minItems = 0,
}: {
  items: T[];
  onChange: (items: T[]) => void;
  newItem: () => T;
  addLabel: string;
  itemLabel: (item: T, index: number) => string;
  renderItem: (item: T, update: (patch: Partial<T>) => void, index: number) => React.ReactNode;
  minItems?: number;
}) {
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className="mlp-list">
      {items.map((item, index) => (
        <div key={index} className="mlp-list-item">
          <div className="mlp-list-item-head">
            <strong>{itemLabel(item, index)}</strong>
            <span className="mlp-list-item-tools">
              <button
                type="button"
                className="mlp-icon-btn"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label="Move up"
              >
                <ArrowUp />
              </button>
              <button
                type="button"
                className="mlp-icon-btn"
                onClick={() => move(index, 1)}
                disabled={index === items.length - 1}
                aria-label="Move down"
              >
                <ArrowDown />
              </button>
              <button
                type="button"
                className="mlp-icon-btn mlp-icon-btn-danger"
                onClick={() => onChange(items.filter((_, i) => i !== index))}
                disabled={items.length <= minItems}
                aria-label="Remove"
              >
                <Trash2 />
              </button>
            </span>
          </div>
          <div className="mlp-grid">
            {renderItem(
              item,
              (patch) => onChange(items.map((it, i) => (i === index ? { ...it, ...patch } : it))),
              index
            )}
          </div>
        </div>
      ))}
      <button type="button" className="mlp-btn mlp-btn-sm mlp-add" onClick={() => onChange([...items, newItem()])}>
        <Plus />
        {addLabel}
      </button>
    </div>
  );
}
